'use client'
import React, { useState } from 'react'
import styled from 'styled-components'
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import StarIcon from '@mui/icons-material/Star'
import GridViewIcon from '@mui/icons-material/GridView'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SearchIcon from '@mui/icons-material/Search'
import RemoveRedEye from '@mui/icons-material/RemoveRedEye'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import ConfigSlider from '@components/Form/ConfigSlider'
import CheckIcon from '@mui/icons-material/Check'
import KeySelector from '@/components/CustomInput/KeySelector'
import { Tooltip } from '@mui/material'
import { VIEWMODEL_GALLERY } from '@/constants/viewmodelGallery'
import { track } from '@/utils/analytics'
import ViewmodelMiniPreview from './ViewmodelMiniPreview'

// Styled Components
const Viewmodel_Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: opacity 0.2s ease;
    width: 100%;
    max-width: 910px;
`

const Viewmodel_ColumnsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
    align-items: stretch;
    width: 100%;
    padding-right: 20px;
`

const Viewmodel_Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    width: 100%;
`

const Viewmodel_HighlightColumn = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    flex: 1;
    min-width: 425px;
    max-width: 445px;
`

const Viewmodel_ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
`

const Viewmodel_PresetBtn = styled.button`
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255, 255, 255, 0.03)'};
    border: 1px solid
        ${({ active }) => (active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.1)')};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.4)')};
    border-radius: 4px;
    padding: 6px 14px;
    font-size: 0.7rem;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s;
    font-weight: 400;
    font-family: var(--font-roboto);
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: white;
        border-color: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.3)'};
    }
`

const Viewmodel_PresetButtonsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 5px;
`

const Viewmodel_TabsHeader = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
    background: rgba(0, 0, 0, 0.2);
    padding: 0;
    border-radius: 8px;
    width: fit-content;
    margin-top: 80px;



`

const Viewmodel_TabButton = styled.button`
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.1)' : 'transparent'};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.4)')};
    border: 1px solid
        ${({ active }) => (active ? 'rgba(99, 102, 241, 0.2)' : 'transparent')};
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 400;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s;
    font-family: var(--font-roboto);
    letter-spacing: 0.5px;
    position: relative;
    overflow: hidden;
    text-transform: uppercase;
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        color: white;
        background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, transparent, #6366f1, transparent);
        z-index: 20;
        opacity: ${({ active }) => (active ? 1 : 0)};
        transition: opacity 0.2s;
    }
`

const Viewmodel_LabelText = styled.span`
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 0.1px;
    display: flex;
    align-items: center;
    cursor: ${({ $isHelp }) => ($isHelp ? 'help' : 'default')};
`

// --- Gallery & Tabs Styled Components (Consistent with Crosshair) ---

const Viewmodel_MainTabButton = styled.button`
    background: ${({ active }) => active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
    color: ${({ active }) => active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
    border: none;
    border-bottom: 2px solid ${({ active }) => active ? '#6366f1' : 'transparent'};
    padding: 10px 20px;
    border-radius: 0;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-chakra);
    letter-spacing: 1px;
    text-transform: uppercase;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        color: #fff;
        background: rgba(99, 102, 241, 0.08);
        border-bottom-color: ${({ active }) => active ? '#6366f1' : 'rgba(99, 102, 241, 0.4)'};
    }
`

const Viewmodel_ModeButton = styled.button`
    background: ${({ active }) => active ? 'rgba(255, 179, 0, 0.15)' : 'rgba(255, 179, 0, 0.05)'};
    color: ${({ active }) => active ? '#ffb300' : 'rgba(255, 255, 255, 0.5)'};
    border: 1px solid ${({ active }) => active ? 'rgba(255, 179, 0, 0.4)' : 'rgba(255, 179, 0, 0.1)'};
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-roboto);
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
        background: ${({ active }) => active ? 'rgba(255, 179, 0, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
        color: ${({ active }) => active ? '#ffca28' : '#fff'};
    }
`

const Viewmodel_ActionButton = styled.button`
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.3);
    padding: 6px 18px;
    border-radius: 20px;
    font-size: 0.65rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-chakra);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transform: translateX(-100%);
        transition: transform 0.6s;
    }

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
        background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
        color: #fff;
        border-color: transparent;

        &::after {
            transform: translateX(100%);
        }

        svg {
            transform: rotate(180deg);
        }
    }
`

const Viewmodel_GalleryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, 170px);
    gap: 11px;
    width: 100%;
    margin-top: 10px;
    max-height: 480px;
    padding-right: 4px;
    justify-content: flex-start;
    min-width: 700px;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(99, 102, 241, 0.3);
        border-radius: 2px;
    }
`

const Viewmodel_GalleryCard = styled.div`
    position: relative;
    width: 170px;
    height: 105px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${({ active }) => active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
    background: ${({ active }) => active ? 'rgba(99, 102, 241, 0.05)' : 'transparent'};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &:hover {
        transform: translateY(-3px);
        border-color: rgba(99, 102, 241, 0.4);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    }
`

const Viewmodel_GalleryInfo = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 8px 12px;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, transparent 100%);
    display: flex;
    flex-direction: column;
    pointer-events: none;
    z-index: 10;
`

const Viewmodel_GalleryPlayer = styled.span`
    font-size: 0.65rem;
    color: #fff;
    font-weight: 600;
    font-family: var(--font-roboto);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 6px;
`

const Viewmodel_GalleryDot = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ff3b6b;
    box-shadow: 0 0 6px rgba(255, 59, 107, 0.5);
`

const Viewmodel_PaginationText = styled.span`
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.4);
    font-family: var(--font-chakra);
    font-weight: 600;
    letter-spacing: 1px;
    margin: 0 8px;
`

const Viewmodel_PaginationCircle = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.08)'};
    border: 1px solid ${({ disabled }) => disabled ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.6)'};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s;
    padding: 0;

    &:hover {
        background: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.15)'};
        color: #fff;
        border-color: rgba(255, 255, 255, 0.2);
        transform: ${({ disabled }) => disabled ? 'none' : 'scale(1.05)'};
    }
`

const Viewmodel_ImportContainer = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;
    min-width: 780px;
`

const Viewmodel_ImportCard = styled.div`
    flex: 1;
    max-width: 320px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(15, 23, 42, 0.4) 100%);
    border: 1px solid rgba(99, 102, 241, 0.15);
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    cursor: default;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;
`

const Viewmodel_ImportIcon = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(99, 102, 241, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(99, 102, 241, 0.6);
    margin-bottom: 4px;
`

const Viewmodel_ImportInput = styled.input`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 8px 12px;
    color: #fff;
    font-size: 0.75rem;
    font-family: var(--font-roboto);
    width: 100%;
    margin-top: 10px;
    outline: none;
    &:focus { border-color: rgba(99, 102, 241, 0.5); }
`

const Viewmodel_ImportTextarea = styled.textarea`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 8px 12px;
    color: #fff;
    font-size: 0.7rem;
    font-family: var(--font-roboto);
    width: 100%;
    height: 80px;
    margin-top: 10px;
    resize: none;
    outline: none;
    &:focus { border-color: rgba(99, 102, 241, 0.5); }
`

const Viewmodel_ExportContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    max-width: 800px;
`

const Viewmodel_ExportGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 20px;
    width: 100%;
`

const Viewmodel_ExportCard = styled.div`
    background: rgba(15, 23, 42, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

// Preview Styles
const Viewmodel_ViewModelPreviewBox = styled.div`
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 425px;
    max-width: 100%;
    height: 300px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    z-index: 105;

    background-image:
        linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
    background-size: 20px 20px;
    box-shadow: inset 0px 0px 100px #05020879;


    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, transparent, #6366f1, transparent);
    }
`

const Viewmodel_GunContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    overflow: hidden;
    /* Simulate Camera Perspective */
    perspective: 800px;
    transform: ${({ isLeftHand }) => (isLeftHand ? 'scaleX(-1)' : 'none')};
    transition: transform 0.3s ease;
`

const Viewmodel_SimpleGunBox = styled.div`
    width: 120px; /* Larger weapon for larger container */
    height: 220px;
    background: linear-gradient(180deg, #334155 0%, #1e293b 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
    transform-origin: 50% 100%;
    transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
    position: relative;
    margin-bottom: -70px; /* Bury it deeper */

    /* Sight indication */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 15px;
        background: rgba(255, 255, 255, 0.2);
    }
`

const Viewmodel_GunText = styled.span`
    color: rgba(255, 255, 255, 0.3);
    font-weight: 400;
    font-size: 0.6rem;
    letter-spacing: 0.1rem;
    text-transform: uppercase;
    font-family: var(--font-roboto);
    margin-bottom: 4px;
`

const Viewmodel_PreviewGunWrapper = styled.div`
    transform: ${({ $transform }) => $transform};
    display: flex;
    flex-direction: column;
    align-items: center;
    transform-origin: 50% 100%;
`

const Viewmodel_PreviewHeader = styled.div`
    position: absolute;
    top: 15px;
    left: 15px;
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const Viewmodel_PreviewTitle = styled.span`
    font-size: 0.7rem;
    color: #6366f1;
    font-weight: 400;
    letter-spacing: 1px;
`

const Viewmodel_PreviewToggles = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    display: flex;
    gap: 6px;
`

const LabelWithTooltip = ({ commandKey, metadata }) => {
    const meta = metadata[commandKey] || { label: commandKey, desc: '' }
    return (
        <Tooltip title={commandKey} placement="top" arrow>
            <Viewmodel_LabelText $isHelp>{meta.label}</Viewmodel_LabelText>
        </Tooltip>
    )
}

export const VIEWMODEL_METADATA = (t) => ({
    viewmodel_offset_x: {
        label: 'OFFSET X',
        desc: t('OFFSET_X_DESC'),
        min: -2.5,
        max: 2.5,
        step: 0.1,
        quick: [-2.5, -1, 0, 1, 2.5],
    },
    viewmodel_offset_y: {
        label: 'OFFSET Y',
        desc: t('OFFSET_Y_DESC'),
        min: -2,
        max: 2,
        step: 0.1,
        quick: [-2, -1, 0, 1, 2],
    },
    viewmodel_offset_z: {
        label: 'OFFSET Z',
        desc: t('OFFSET_Z_DESC'),
        min: -2,
        max: 2,
        step: 0.1,
        quick: [-2, -1, 0, 1, 2],
    },
    viewmodel_fov: {
        label: 'FOV',
        desc: t('FOV_DESC'),
        min: 54,
        max: 68,
        step: 1,
        quick: [54, 58, 60, 64, 68],
    },
})

const GEN_PRESETS = [
    { name: 'Desktop', x: 1, y: 1, z: -1, fov: 60 },
    { name: 'Couch', x: 0, y: 0, z: 0, fov: 54 },
    { name: 'Classic', x: 2.5, y: 0, z: -1.5, fov: 68 },
    { name: 'Doom', x: -2.5, y: 2, z: -2, fov: 68 },
    { name: 'Quake', x: -2.5, y: -2, z: -2, fov: 68 },
    { name: 'Gangster', x: 2.5, y: 0, z: 2, fov: 68 },
    { name: 'Retro 1.6', x: 2.5, y: 2, z: -1.5, fov: 68 },
    { name: 'GoldenEye', x: 0, y: -2, z: -1, fov: 60 },
    { name: 'RoboCop', x: 0.5, y: 2, z: 2, fov: 68 },
    { name: 'Predator', x: 2.5, y: -2, z: 2, fov: 68 },
    { name: 'Cinematic', x: 2.5, y: 2, z: -2, fov: 68 },
    { name: 'Minimalist', x: 2.5, y: -2, z: -2, fov: 54 },
    { name: 'Half-Life', x: 2, y: 0.5, z: -1, fov: 65 },
    { name: 'Modern CS', x: 1.5, y: 1.5, z: -1.5, fov: 65 },
]

const PLAYER_PRESETS = [
    { name: 's1mple', x: 2.5, y: 0, z: -1.5, fov: 68 },
    { name: 'ZywOo', x: 2.5, y: 0, z: -1.5, fov: 68 },
    { name: 'NiKo', x: 2.5, y: 0, z: -1.5, fov: 68 },
    { name: 'm0NESY', x: 2.5, y: 0, z: -1.5, fov: 68 },
    { name: 'donk', x: 2.5, y: 0, z: -1.5, fov: 68 },
    { name: 'ropz', x: 2, y: 2, z: -2, fov: 68 },
    { name: 'KennyS', x: 2, y: 2, z: -2, fov: 68 },
    { name: 'XANTARES', x: 1, y: 1, z: -1, fov: 60 },
    { name: 'EliGE', x: 1, y: 2, z: -1.5, fov: 68 },
    { name: 'apEX', x: 2, y: 2, z: -2, fov: 60 },
    { name: 'sh1ro', x: 2.5, y: 1, z: -2, fov: 68 },
    { name: 'YEKINDAR', x: -0.5, y: 1, z: -2, fov: 65 },
    { name: 'iM', x: 1, y: 1, z: -2, fov: 60 },
]

export default function ViewmodelModule({ id, initialExpanded }) {
    const { t } = useLanguage()
    const { activeModules, configData, updateConfig, configIsLocked } = useConfig()
    const metadata = VIEWMODEL_METADATA(t)
    const isActive = activeModules.includes('viewmodel')
    // Tabs state
    const [activeMainTab, setActiveMainTab] = useState('gallery')
    const [activeGalleryMode, setActiveGalleryMode] = useState('preset')
    const [galleryPage, setGalleryPage] = useState(0)
    const [searchTerm, setSearchTerm] = useState('')
    const [randomGallery, setRandomGallery] = useState([])

    // Import/Export state
    const [cfgInput, setCfgInput] = useState('')

    // Fallback
    const vm = configData.viewmodel || {
        viewmodel_offset_x: 1,
        viewmodel_offset_y: 1,
        viewmodel_offset_z: -1,
        viewmodel_fov: 60,
        cl_prefer_lefthanded: 0,
    }

    const commandString = `viewmodel_offset_x ${vm.viewmodel_offset_x}; viewmodel_offset_y ${vm.viewmodel_offset_y}; viewmodel_offset_z ${vm.viewmodel_offset_z}; viewmodel_fov ${vm.viewmodel_fov}; cl_prefer_lefthanded ${vm.cl_prefer_lefthanded};`

    const handleChange = (key, value) => updateConfig('viewmodel', key, value)

    const parseCommandString = (str) => {
        if (!str) return
        str.split(';').forEach(cmd => {
            const parts = cmd.trim().split(' ')
            if (parts.length < 2) return
            const key = parts[0]
            const val = parseFloat(parts[1])
            if (key === 'viewmodel_offset_x' || key === 'viewmodel_offset_y' || key === 'viewmodel_offset_z' || key === 'viewmodel_fov' || key === 'cl_prefer_lefthanded') {
                updateConfig('viewmodel', key, val)
            }
        })
    }

    const generateRandom = () => {
        const newRandoms = []
        for (let i = 0; i < 20; i++) {
            const x = (Math.random() * 5 - 2.5).toFixed(1)
            const y = (Math.random() * 4 - 2).toFixed(1)
            const z = (Math.random() * 4 - 2).toFixed(1)
            const fov = Math.floor(Math.random() * (68 - 54 + 1) + 54)
            newRandoms.push({
                id: `random-${i}-${Date.now()}`,
                name: `Random ${i + 1}`,
                player: 'Procedural',
                commands: `viewmodel_offset_x ${x}; viewmodel_offset_y ${y}; viewmodel_offset_z ${z}; viewmodel_fov ${fov};`,
                category: 'random'
            })
        }
        setRandomGallery(newRandoms)
        setActiveGalleryMode('random')
        setGalleryPage(0)
    }

    const isPresetActive = (cmds) => {
        const p = {}
        cmds.split(';').forEach(c => {
            const parts = c.trim().split(' ')
            if (parts.length >= 2) p[parts[0]] = parseFloat(parts[1])
        })
        return vm.viewmodel_offset_x === p.viewmodel_offset_x &&
            vm.viewmodel_offset_y === p.viewmodel_offset_y &&
            vm.viewmodel_offset_z === p.viewmodel_offset_z &&
            vm.viewmodel_fov === p.viewmodel_fov
    }

    // --- Transform Logic ---
    // CS2 Limits:
    // X: -2.5 (Left) to 2.5 (Right)
    // Y: -2.0 (Close) to 2.0 (Far)
    // Z: -2.0 (Down) to 2.0 (Up)

    // Base X shift: Optimized for 425px width.
    // Centered at -2.5. Max right (2.5) results in 5 * 37 = 185 (fits well in 212.5px half-width).
    const pixelX = (vm.viewmodel_offset_x + 2.5) * 37
    const pixelY = -vm.viewmodel_offset_z * 8

    // FOV Effect:
    const fovScale = 1 - (vm.viewmodel_fov - 60) * 0.02

    // Viewmodel Offset Y (Depth)
    const depthScale = 1 - vm.viewmodel_offset_y * 0.05
    const totalScale = fovScale * depthScale

    // Compensate scale:
    const scaleYComp =
        totalScale > 1 ? (totalScale - 1) * 200 : (totalScale - 1) * 80

    // Additional "Forward" shift for Y offset
    const depthTranslateY = -vm.viewmodel_offset_y * 10

    const finalTransform = `translate(${pixelX}px, ${pixelY + depthTranslateY + scaleYComp}px) scale(${totalScale})`

    return (
        <ModuleContainer
            id={id}
            moduleKey="viewmodel"
            title={t('VIEWMODEL_TITLE')}
            description={t('VIEWMODEL_DESC')}
            icon={DisplaySettingsIcon}
            initialExpanded={initialExpanded}
        >
            <Viewmodel_Controls isActive={isActive}>
                <Viewmodel_ColumnsContainer>
                    <Viewmodel_HighlightColumn>
                        <Viewmodel_ViewModelPreviewBox>
                            <Viewmodel_GunContainer isLeftHand={vm.cl_prefer_lefthanded === 1}>
                                <Viewmodel_PreviewGunWrapper $transform={finalTransform}>
                                    <Viewmodel_GunText>{t('WEAPON')}</Viewmodel_GunText>
                                    <Viewmodel_SimpleGunBox />
                                </Viewmodel_PreviewGunWrapper>
                            </Viewmodel_GunContainer>
                            <Viewmodel_PreviewHeader>
                                <Viewmodel_PreviewTitle>{t('PREVIEW')}</Viewmodel_PreviewTitle>
                            </Viewmodel_PreviewHeader>
                            <Viewmodel_PreviewToggles>
                                <Viewmodel_PresetBtn active={vm.cl_prefer_lefthanded === 1} onClick={() => !configIsLocked && handleChange('cl_prefer_lefthanded', 1)} disabled={configIsLocked}>
                                    {t('LEFT_HAND')}
                                </Viewmodel_PresetBtn>
                                <Viewmodel_PresetBtn active={vm.cl_prefer_lefthanded === 0} onClick={() => !configIsLocked && handleChange('cl_prefer_lefthanded', 0)} disabled={configIsLocked}>
                                    {t('RIGHT_HAND')}
                                </Viewmodel_PresetBtn>
                            </Viewmodel_PreviewToggles>
                        </Viewmodel_ViewModelPreviewBox>
                    </Viewmodel_HighlightColumn>

                    <Viewmodel_Column>
                        <Viewmodel_ControlGroup>
                            {Object.entries(metadata).map(([key, meta]) => (
                                <ConfigSlider
                                    key={key}
                                    label={meta.label}
                                    description={meta.desc}
                                    min={meta.min}
                                    max={meta.max}
                                    step={meta.step}
                                    value={vm[key]}
                                    onChange={(val) => handleChange(key, val)}
                                    presets={meta.quick}
                                    showPresetsInline={true}
                                    presetsPlacement="header"
                                    disabled={configIsLocked}
                                />
                            ))}
                        </Viewmodel_ControlGroup>
                    </Viewmodel_Column>
                </Viewmodel_ColumnsContainer>

                <Viewmodel_TabsHeader>
                    <Viewmodel_MainTabButton active={activeMainTab === 'gallery'} onClick={() => setActiveMainTab('gallery')}>GALERÍA</Viewmodel_MainTabButton>
                    <Viewmodel_MainTabButton active={activeMainTab === 'import'} onClick={() => setActiveMainTab('import')}>IMPORTAR</Viewmodel_MainTabButton>
                    <Viewmodel_MainTabButton active={activeMainTab === 'export'} onClick={() => setActiveMainTab('export')}>EXPORTAR</Viewmodel_MainTabButton>
                </Viewmodel_TabsHeader>

                {activeMainTab === 'gallery' && (() => {
                    const baseData = activeGalleryMode === 'random' ? randomGallery : VIEWMODEL_GALLERY.filter(item => item.category === activeGalleryMode);
                    const filtered = baseData.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.player.toLowerCase().includes(searchTerm.toLowerCase()));
                    const totalPages = Math.ceil(filtered.length / 10);

                    return (
                        <Viewmodel_Column>
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Viewmodel_ActionButton onClick={generateRandom}>
                                        <ShuffleIcon style={{ fontSize: '1.1rem' }} />
                                        GENERAR RANDOM
                                    </Viewmodel_ActionButton>
                                    <Viewmodel_ModeButton active={activeGalleryMode === 'pro'} onClick={() => { setActiveGalleryMode('pro'); setGalleryPage(0) }}>
                                        <StarIcon style={{ fontSize: '1rem' }} />
                                        PRO PLAYERS
                                    </Viewmodel_ModeButton>
                                    <Viewmodel_ModeButton active={activeGalleryMode === 'preset'} onClick={() => { setActiveGalleryMode('preset'); setGalleryPage(0) }}>
                                        <GridViewIcon style={{ fontSize: '1rem' }} />
                                        PRESETS
                                    </Viewmodel_ModeButton>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
                                    {activeGalleryMode !== 'random' && (
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginLeft: '20px' }}>
                                            <SearchIcon style={{ position: 'absolute', left: '10px', fontSize: '1rem', color: 'rgba(255,255,255,0.2)' }} />
                                            <Viewmodel_ImportInput
                                                placeholder="Buscar..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{ width: '180px', marginTop: 0, paddingLeft: '32px' }}
                                            />
                                        </div>
                                    )}

                                    {(activeGalleryMode === 'pro' || activeGalleryMode === 'preset') && totalPages > 1 && (
                                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                            <Viewmodel_PaginationCircle disabled={galleryPage === 0} onClick={() => setGalleryPage(p => p - 1)}>
                                                <ChevronLeftIcon />
                                            </Viewmodel_PaginationCircle>
                                            <Viewmodel_PaginationText>{galleryPage + 1} / {totalPages}</Viewmodel_PaginationText>
                                            <Viewmodel_PaginationCircle disabled={galleryPage >= totalPages - 1} onClick={() => setGalleryPage(p => p + 1)}>
                                                <ChevronRightIcon />
                                            </Viewmodel_PaginationCircle>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Viewmodel_GalleryGrid>
                                {filtered.slice(galleryPage * 10, (galleryPage + 1) * 10).map((item) => (
                                    <Viewmodel_GalleryCard key={item.id} active={isPresetActive(item.commands)} onClick={() => { track('preset_use', { kind: 'viewmodel', id: item.id }); parseCommandString(item.commands) }}>
                                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                            <ViewmodelMiniPreview commands={item.commands} />

                                            {/* {item.category !== 'random' && (
                                                <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 20 }} onClick={(e) => e.stopPropagation()}>
                                                    <Tooltip
                                                        title={
                                                            <div style={{ padding: '2px' }}>
                                                                <img
                                                                    src={item.image}
                                                                    alt={item.name}
                                                                    style={{ width: '280px', height: 'auto', borderRadius: '4px', display: 'block' }}
                                                                />
                                                            </div>
                                                        }
                                                        placement="left"
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    bgcolor: 'rgba(15, 23, 42, 0.95)',
                                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                                    p: 0,
                                                                    maxWidth: 'none',
                                                                    boxShadow: '0 10px 40px rgba(0,0,0,0.8)'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <div style={{
                                                            background: 'rgba(0,0,0,0.6)',
                                                            borderRadius: '50%',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'help',
                                                            border: '1px solid rgba(255,255,255,0.1)',
                                                            backdropFilter: 'blur(4px)'
                                                        }}>
                                                            <RemoveRedEye style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }} />
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            )} */}
                                        </div>
                                        <Viewmodel_GalleryInfo>
                                            <Viewmodel_GalleryPlayer>
                                                {item.player === 'Procedural' ? (
                                                    <ShuffleIcon style={{ fontSize: '0.9rem', color: '#ff3b6b', opacity: 0.8 }} />
                                                ) : (
                                                    <>
                                                        <Viewmodel_GalleryDot />
                                                        {item.player !== 'Preset' ? item.player : item.name}
                                                    </>
                                                )}
                                            </Viewmodel_GalleryPlayer>
                                        </Viewmodel_GalleryInfo>
                                    </Viewmodel_GalleryCard>
                                ))}
                            </Viewmodel_GalleryGrid>
                        </Viewmodel_Column>
                    );
                })()}

                {activeMainTab === 'import' && (
                    <Viewmodel_ImportContainer>
                        <Viewmodel_ImportCard>
                            <Viewmodel_ImportIcon>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                            </Viewmodel_ImportIcon>
                            <h4 style={{ margin: 0, fontFamily: 'var(--font-chakra)', color: '#fff', fontSize: '0.9rem' }}>Comandos Viewmodel</h4>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>Pega tus comandos viewmodel_offset...</p>
                            <Viewmodel_ImportTextarea
                                placeholder="viewmodel_offset_x 2.5; ..."
                                value={cfgInput}
                                onChange={(e) => setCfgInput(e.target.value)}
                            />
                            <Viewmodel_ActionButton
                                style={{ width: '100%', marginTop: '10px' }}
                                onClick={() => { parseCommandString(cfgInput); setCfgInput(''); setActiveMainTab('settings'); }}
                                disabled={!cfgInput.trim()}
                            >
                                Importar
                            </Viewmodel_ActionButton>
                        </Viewmodel_ImportCard>
                    </Viewmodel_ImportContainer>
                )}

                {activeMainTab === 'export' && (
                    <Viewmodel_ExportContainer>
                        <Viewmodel_ExportGrid>
                            <Viewmodel_ExportCard>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '8px', borderRadius: '8px' }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
                                    </div>
                                    <h4 style={{ margin: 0, fontFamily: 'var(--font-chakra)', textTransform: 'uppercase', fontSize: '0.9rem', color: '#fff' }}>Copiar Comandos</h4>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>Copia los comandos para la consola.</p>
                                <Viewmodel_ImportInput readOnly value={commandString} style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', fontSize: '0.7rem' }} onClick={(e) => e.target.select()} />
                                <Viewmodel_ActionButton
                                    style={{ width: '100%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff' }}
                                    onClick={() => navigator.clipboard.writeText(commandString)}
                                >
                                    Copiar al portapapeles
                                </Viewmodel_ActionButton>
                            </Viewmodel_ExportCard>

                            <Viewmodel_ExportCard>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h4 style={{ margin: 0, fontFamily: 'var(--font-chakra)', textTransform: 'uppercase', fontSize: '0.9rem', color: '#fff' }}>Vista previa CFG</h4>
                                </div>
                                <Viewmodel_ImportTextarea readOnly value={commandString.replace(/; /g, '\n')} style={{ height: '120px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', margin: 0 }} />
                            </Viewmodel_ExportCard>
                        </Viewmodel_ExportGrid>
                    </Viewmodel_ExportContainer>
                )}


            </Viewmodel_Controls>
        </ModuleContainer>
    )
}
